import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Eye, EyeOff, MapPin, ArrowLeft, CheckCircle, Clock, UserPlus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import WaitlistModal from "@/components/WaitlistModal";

type EarlyAdopterStep = 'enter' | 'approved' | 'pending' | 'not-found';

const Auth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('signup');
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  
  // Early adopter flow state
  const [isEarlyAdopterMode, setIsEarlyAdopterMode] = useState(false);
  const [earlyAdopterStep, setEarlyAdopterStep] = useState<EarlyAdopterStep>('enter');
  const [checkEmail, setCheckEmail] = useState('');
  const [waitlistModalOpen, setWaitlistModalOpen] = useState(false);
  
  const planParam = searchParams.get('plan');
  const emailParam = searchParams.get('email');
  const earlyParam = searchParams.get('early');
  const showPlanContext = planParam === 'adventurer';
  
  const [signInData, setSignInData] = useState({
    email: '',
    password: ''
  });
  
  const [signUpData, setSignUpData] = useState({
    email: emailParam || '',
    password: ''
  });

  useEffect(() => {
    // Check URL hash for recovery mode FIRST (before any redirects)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');
    if (type === 'recovery') {
      setIsRecoveryMode(true);
      return; // Don't check for existing session - we want to show reset form
    }

    // Check if early adopter mode
    if (earlyParam === 'true') {
      setIsEarlyAdopterMode(true);
      return; // Don't redirect - show email check flow
    }

    // Listen for PASSWORD_RECOVERY event (backup detection)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecoveryMode(true);
      }
    });

    // Check if user is already logged in (only if NOT in recovery mode)
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Check if onboarding is completed
        const { data: userData } = await supabase
          .from('users')
          .select('onboarding_completed_at')
          .eq('id', session.user.id)
          .single();
        
        if (userData?.onboarding_completed_at) {
          navigate('/activity');
        } else {
          navigate('/onboarding');
        }
      }
    };
    
    // Defer the check to avoid auth deadlock
    setTimeout(checkUser, 0);

    return () => subscription.unsubscribe();
  }, [navigate, earlyParam]);

  const checkWaitlistStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const normalizedEmail = checkEmail.toLowerCase().trim();
      
      const { data, error } = await supabase
        .from('waitlist_signups')
        .select('email, wave_number, invited_at')
        .eq('email', normalizedEmail)
        .maybeSingle();

      if (error) {
        setError('Something went wrong. Please try again.');
        return;
      }

      if (data && data.wave_number !== null) {
        // Approved - check if they already have an account
        setSignUpData({ ...signUpData, email: normalizedEmail });
        setSignInData({ ...signInData, email: normalizedEmail });
        
        // Check if account exists to determine default tab
        try {
          const response = await supabase.functions.invoke('check-account-exists', {
            body: { email: normalizedEmail }
          });
          
          if (response.data?.exists) {
            setActiveTab('signin');
          } else {
            setActiveTab('signup');
          }
        } catch {
          // If check fails, default to sign up
          setActiveTab('signup');
        }
        
        setEarlyAdopterStep('approved');
      } else if (data) {
        // On waitlist but not invited yet
        setEarlyAdopterStep('pending');
      } else {
        // Not on waitlist
        setEarlyAdopterStep('not-found');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: signInData.email,
        password: signInData.password,
      });

      if (error) {
        setError(error.message);
      } else {
        // Check if onboarding is completed before deciding where to redirect
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const { data: userData } = await supabase
            .from('users')
            .select('onboarding_completed_at')
            .eq('id', session.user.id)
            .single();
          
          if (userData?.onboarding_completed_at) {
            toast({
              title: "Welcome back!",
              description: "You've been successfully signed in.",
            });
            navigate('/activity');
          } else {
            navigate('/onboarding');
          }
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const { data, error } = await supabase.auth.signUp({
        email: signUpData.email,
        password: signUpData.password,
      });

      if (error) {
        // Handle different error types with user-friendly messages
        if (error.message?.includes('timeout') || error.status === 504) {
          setError('The server is taking too long to respond. Your account may have been created - please try signing in.');
        } else if (error.message) {
          setError(error.message);
        } else {
          setError('An error occurred during signup. Please try again.');
        }
      } else if (data.session) {
        // User is automatically logged in - redirect to onboarding
        toast({
          title: "Account created!",
          description: "Welcome to Airing Down.",
        });
        navigate('/onboarding');
      } else {
        // Fallback if session not immediately available
        setError('Account created but login failed. Please try signing in.');
      }
    } catch (err: any) {
      // Handle network/timeout errors
      if (err?.message?.includes('timeout') || err?.message?.includes('504')) {
        setError('The request timed out. Your account may have been created - please try signing in.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotPasswordEmail, {
        redirectTo: 'https://airingdown.com/auth',
      });

      if (error) {
        setError(error.message);
      } else {
        setForgotPasswordSent(true);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) {
        setError(error.message);
      } else {
        toast({
          title: "Password updated!",
          description: "Your password has been successfully reset.",
        });
        setIsRecoveryMode(false);
        navigate('/activity');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Password Recovery Mode UI
  if (isRecoveryMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-primary/5 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="h-8 w-8 text-primary" />
              <h1 className="text-2xl font-bold text-foreground">Airing Down</h1>
            </div>
            <p className="text-muted-foreground">Set your new password</p>
          </div>

          <Card className="border-border/50 shadow-lg">
            <CardHeader>
              <CardTitle>Reset Password</CardTitle>
              <CardDescription>
                Enter your new password below
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                    <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? "Updating..." : "Update Password"}
                </Button>
              </form>

              {error && (
                <Alert className="mt-4 border-destructive/50 text-destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Forgot Password Mode UI
  if (isForgotPasswordMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-primary/5 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="h-8 w-8 text-primary" />
              <h1 className="text-2xl font-bold text-foreground">Airing Down</h1>
            </div>
            <p className="text-muted-foreground">Reset your password</p>
          </div>

          <Card className="border-border/50 shadow-lg">
            <CardHeader>
              <CardTitle>Forgot Password</CardTitle>
              <CardDescription>
                Enter your email and we'll send you a reset link
              </CardDescription>
            </CardHeader>
            <CardContent>
              {forgotPasswordSent ? (
                <div className="text-center py-4">
                  <CheckCircle className="h-12 w-12 text-primary mx-auto mb-4" />
                  <p className="text-foreground font-medium mb-2">Check your email</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    We've sent a password reset link to <strong>{forgotPasswordEmail}</strong>
                  </p>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setIsForgotPasswordMode(false);
                      setForgotPasswordSent(false);
                      setForgotPasswordEmail('');
                      setActiveTab('signin');
                    }}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Sign In
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="your@email.com"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Sending..." : "Send Reset Link"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => setIsForgotPasswordMode(false)}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Sign In
                  </Button>
                </form>
              )}

              {error && (
                <Alert className="mt-4 border-destructive/50 text-destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Early Adopter Mode - Email Check
  if (isEarlyAdopterMode && earlyAdopterStep === 'enter') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-primary/5 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="h-8 w-8 text-primary" />
              <h1 className="text-2xl font-bold text-foreground">Airing Down</h1>
            </div>
            <p className="text-muted-foreground">Early Adopter Access</p>
          </div>

          <Card className="border-border/50 shadow-lg">
            <CardHeader>
              <CardTitle>Check Your Access</CardTitle>
              <CardDescription>
                Enter your email to check your early adopter status
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={checkWaitlistStatus} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="check-email">Email</Label>
                  <Input
                    id="check-email"
                    type="email"
                    placeholder="your@email.com"
                    value={checkEmail}
                    onChange={(e) => setCheckEmail(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? "Checking..." : "Check Access"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => navigate('/')}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
              </form>

              {error && (
                <Alert className="mt-4 border-destructive/50 text-destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Early Adopter Mode - Pending Approval
  if (isEarlyAdopterMode && earlyAdopterStep === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-primary/5 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="h-8 w-8 text-primary" />
              <h1 className="text-2xl font-bold text-foreground">Airing Down</h1>
            </div>
          </div>

          <Card className="border-border/50 shadow-lg">
            <CardHeader className="text-center">
              <Clock className="h-12 w-12 text-primary mx-auto mb-2" />
              <CardTitle>Almost There!</CardTitle>
              <CardDescription>
                Thanks for your patience, you're on the waitlist!
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                We're rolling out access in waves. Keep an eye on your inbox — your turn is coming soon.
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Early Adopter Mode - Not on Waitlist
  if (isEarlyAdopterMode && earlyAdopterStep === 'not-found') {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-primary/5 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                <MapPin className="h-8 w-8 text-primary" />
                <h1 className="text-2xl font-bold text-foreground">Airing Down</h1>
              </div>
            </div>

            <Card className="border-border/50 shadow-lg">
              <CardHeader className="text-center">
                <UserPlus className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                <CardTitle>Not on the List Yet</CardTitle>
                <CardDescription>
                  We couldn't find that email on our waitlist.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground text-center">
                  Join the waitlist to get early access when spots open up!
                </p>
                <Button
                  className="w-full"
                  onClick={() => setWaitlistModalOpen(true)}
                >
                  Join the Waitlist
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/')}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <WaitlistModal 
          open={waitlistModalOpen} 
          onOpenChange={setWaitlistModalOpen} 
        />
      </>
    );
  }

  // Main Sign In / Sign Up UI (shown when approved in early adopter mode OR regular auth)
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-primary/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <MapPin className="h-8 w-8 text-primary" />
            <h1 className="text-2xl font-bold text-foreground">Airing Down</h1>
          </div>
          <p className="text-muted-foreground">Join the off-road adventure community</p>
          
          {/* Early adopter approved badge */}
          {isEarlyAdopterMode && earlyAdopterStep === 'approved' && (
            <div className="mt-4 p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <Badge className="mb-2 bg-primary text-primary-foreground">✓ Early Adopter Verified</Badge>
              <p className="text-sm text-muted-foreground">
                Welcome! You have early access to Airing Down.
              </p>
            </div>
          )}
          
          {/* Tier context messaging */}
          {showPlanContext && !isEarlyAdopterMode && (
            <div className="mt-4 p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <Badge className="mb-2 bg-primary text-primary-foreground">Adventurer Plan Selected</Badge>
              <p className="text-sm text-muted-foreground">
                Create your free account first, then complete your Adventurer subscription.
              </p>
            </div>
          )}
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardHeader>
            <CardTitle>Access Your Adventure Hub</CardTitle>
            <CardDescription>
              Sign in to track your trails, earn badges, and connect with the community
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>
              
              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="your@email.com"
                      value={signInData.email}
                      onChange={(e) => setSignInData({ ...signInData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="signin-password">Password</Label>
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={() => {
                          setIsForgotPasswordMode(true);
                          setError('');
                        }}
                      >
                        Forgot Password?
                      </button>
                    </div>
                    <div className="relative">
                      <Input
                        id="signin-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={signInData.password}
                        onChange={(e) => setSignInData({ ...signInData, password: e.target.value })}
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Signing In..." : "Sign In"}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="your@email.com"
                      value={signUpData.email}
                      onChange={(e) => setSignUpData({ ...signUpData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="signup-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Create a password"
                        value={signUpData.password}
                        onChange={(e) => setSignUpData({ ...signUpData, password: e.target.value })}
                        required
                        minLength={6}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Creating Account..." : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            {error && (
              <Alert className="mt-4 border-destructive/50 text-destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
        
        
        <div className="text-center mt-4 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded-full font-medium">BETA</span>
            Airing Down is in active development.
          </span>
          <br />
          By signing up, you agree to our{' '}
          <Link to="/terms" className="underline hover:text-foreground">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
        </div>
      </div>
    </div>
  );
};

export default Auth;
